import type { ItemView, LearnerClient } from "@tutor/live";
import { useId, useState, type SubmitEvent } from "react";
import { useT } from "../i18n";
import { LessonBoard } from "./LessonBoard";

// « Je vérifie » (lesson first, step 2): after Coach taught the idea, the lesson card's
// quick question, answered right here and graded by rules in the actor. The engine says
// what comes next: the exercise after a right answer, a second try, or one smaller teach
// from Coach. « Passe à l'exercice » is always there (ADR-0044: no dead ends).

export function LessonCheck(props: {
  item: ItemView;
  client: Pick<LearnerClient, "checkLesson" | "skipLesson">;
}) {
  const { item, client } = props;
  const t = useT();
  const titleId = useId();
  const inputId = useId();
  const [draft, setDraft] = useState("");
  const lesson = item.lesson;
  if (lesson === undefined || lesson.done) return null;
  const last = lesson.answers.at(-1);
  const skip = (
    <button
      type="button"
      className="button button-quiet lesson-check-skip"
      onClick={() => {
        client.skipLesson(item.itemId);
      }}
    >
      {t("board.lessonCheck.skip")}
    </button>
  );
  const question = lesson.question;
  // ADR-0065: Coach teaches first; the exercise waits under this note.
  if (question === null) {
    // The board writes the card while Coach walks the child through it (point 9).
    if (lesson.page !== null) {
      return (
        <div className="lesson-written">
          <LessonBoard page={lesson.page} />
          {skip}
        </div>
      );
    }
    return (
      <section className="lesson-check lesson-teaching" aria-labelledby={titleId}>
        <h3 id={titleId} className="lesson-check-title">
          {t("board.lessonTeaching.title")}
        </h3>
        <p className="lesson-teaching-note" role="status">
          {t("board.lessonTeaching.note")}
        </p>
        {skip}
      </section>
    );
  }

  const send = (answer: string) => {
    if (answer.trim() === "") return;
    client.checkLesson(item.itemId, answer);
    setDraft("");
  };
  const onSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    send(draft);
  };

  const check = (
    <section className="lesson-check" aria-labelledby={titleId}>
      <h3 id={titleId} className="lesson-check-title">
        {t("board.lessonCheck.title")}
      </h3>
      <p className="lesson-check-question">{question}</p>
      {lesson.choices.length > 0 ? (
        <div className="lesson-check-choices" role="group" aria-labelledby={titleId}>
          {lesson.choices.map((choice, index) => (
            <button
              key={choice}
              type="button"
              className="button"
              onClick={() => {
                send(String(index));
              }}
            >
              {choice}
            </button>
          ))}
        </div>
      ) : (
        <form className="lesson-check-form" onSubmit={onSubmit}>
          <label htmlFor={inputId} className="lesson-check-label">
            {t("board.lessonCheck.answer")}
          </label>
          <input
            id={inputId}
            className="lesson-check-input"
            inputMode="decimal"
            autoComplete="off"
            maxLength={40}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
          <button type="submit" className="button" disabled={draft.trim() === ""}>
            {t("board.lessonCheck.send")}
          </button>
        </form>
      )}
      {last !== undefined && !last.right && (
        <p className="lesson-check-status" role="status">
          {t("board.lessonCheck.again")}
        </p>
      )}
      {skip}
    </section>
  );
  // The lesson stays written above « Je vérifie », for the child to look back at.
  return lesson.page === null ? (
    check
  ) : (
    <div className="lesson-written">
      <LessonBoard page={lesson.page} animate={false} />
      {check}
    </div>
  );
}
